import React, { useEffect, useState } from "react";
import { Modal, Table, Form, Button, Input } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import routes from "../../api/routes";
import { callApi } from "../../api/apiCaller";
import Loader from "../../components/loader/loader";
import { GreenNotify, RedNotify } from "../../helper/helper";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import {
  setPolicies,
  setPrivacyPolicyLoading,
  setPrivacyPolicyError,
  deletePolicy as deletePolicyAction,
} from "../../redux/privacyPolicySlice";
import { getBodyPlainText } from "../../utils/getPlainTextFromHtml";

// 1. The main HTML shell with a placeholder for the content.
const defaultHtmlTemplate = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Privacy Policy – FITBIZZ </title>
    <style>
        body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            margin: 0;
            padding: 0;
            background-color: #f4f4f4;
            color: #333;
        }
        .container {
            width: 80%;
            margin: 0 auto;
            padding: 20px;
            background-color: #fff;
        }
        h1, h2 {
            font-weight: bold;
            color: #333;
        }
        p {
            font-size: 16px;
            margin-bottom: 1em;
        }
        ul {
            margin-bottom: 1em;
            padding-left: 20px;
        }
        ul li {
            margin-bottom: 0.5em;
        }
        a {
            color: #007bff;
            text-decoration: none;
        }
        a:hover {
            text-decoration: underline;
        }
        em {
            font-style: italic;
        }
    </style>
</head>
<body>
    <div class="container">
    <!--USER_CONTENT_HERE-->
        </div>
</body>
</html>`;

// 2. The default content to pre-fill the Quill editor for a new policy.

const PrivacyPolicy = () => {
  const dispatch = useDispatch();
  const policies = useSelector((state) => state.privacyPolicy?.policies);
  const isLoading = useSelector((state) => state.privacyPolicy?.isLoading);
  const error = useSelector((state) => state.privacyPolicy?.error);

  const [searchText, setSearchText] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isEditingMode, setIsEditingMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchPolicies = () => {
    dispatch(setPrivacyPolicyLoading(true));
    callApi(
      "GET",
      routes.getPrivacyPolicies,
      null,
      (loading) => dispatch(setPrivacyPolicyLoading(loading)),
      (res) => {
        const policy = res?.data;
        dispatch(setPolicies(policy ? [policy] : []));
      },
      (err) => {
        dispatch(setPrivacyPolicyError("Failed to fetch privacy policies."));
        dispatch(setPolicies([]));
      }
    );
  };

  const handleCreate = () => {
    setEditingId(null);
    setIsEditingMode(false);
    // Use the default *content* template for the editor
    form.setFieldsValue({ content: "" });
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    try {
      // Extract the user content from the full HTML to populate the editor
      const parser = new DOMParser();
      const doc = parser.parseFromString(record.content, "text/html");
      const container = doc.querySelector(".container");
      const userContent = container ? container.innerHTML : "";

      form.setFieldsValue({ content: userContent });
    } catch (error) {
      console.error("Failed to parse policy content for editing:", error);
      // Fallback to showing the full content if parsing fails
      form.setFieldsValue({ content: record.content });
    }

    setEditingId(record._id);
    setIsEditingMode(true);
    setIsModalVisible(true);
  };

  const handleDelete = (id) => {
    callApi(
      "DELETE",
      `${routes.deletePrivacyPolicy}/${id}`,
      null,
      () => {},
      (res) => {
        GreenNotify("Privacy Policy deleted successfully");
        dispatch(deletePolicyAction(id));
        fetchPolicies();
      },
      (err) => RedNotify("Failed to delete privacy policy")
    );
  };

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        const { content } = values;
        let finalContent = content;

        if (!content.trim().toLowerCase().includes("<html")) {
          // ✅ Correctly replace the placeholder with the editor content
          finalContent = defaultHtmlTemplate.replace(
            "<!--USER_CONTENT_HERE-->",
            content
          );
        }
        const method = isEditingMode ? "PUT" : "POST";
        const url = isEditingMode
          ? `${routes.updatePrivacyPolicy}/${editingId}`
          : routes.createPrivacyPolicy;

        const body = { ...values, content: finalContent };

        callApi(
          method,
          url,
          body, // Send the final combined HTML
          () => {},
          (res) => {
            GreenNotify(
              isEditingMode
                ? "Privacy Policy updated successfully"
                : "Privacy Policy created successfully"
            );
            fetchPolicies();
            setIsModalVisible(false);
            setIsSubmitting(false);
          },
          (err) => {
            RedNotify("Failed to submit privacy policy");
            setIsSubmitting(false);
          }
        );
      })
      .catch(() => {
        RedNotify("Please fill all the fields correctly.");
      });
  };

  useEffect(() => {
    if (!policies || policies.length === 0) {
      fetchPolicies();
    }
  }, []);

  const filteredData = policies
    ?.filter((policy) =>
      getBodyPlainText(policy?.content, 150)
        ?.toLowerCase()
        .includes(searchText.toLowerCase())
    )
    .map((policy) => ({
      ...policy,
      key: policy?._id,
      createdAt: moment(policy?.createdAt).format("MMMM Do, YYYY, h:mm:ss A"),
    }));

  const columns = [
    {
      title: "Content",
      dataIndex: "content",
      align: "left",
      render: (text) => getBodyPlainText(text, 150),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      align: "center",
    },
    {
      title: "Actions",
      align: "center",
      render: (_, record) => (
        <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
          <EditOutlined
            style={{ color: "blue", cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(record);
            }}
          />
          {/* <DeleteOutlined
            style={{ color: "red", cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(record._id);
            }}
          /> */}
        </div>
      ),
    },
  ];

  return (
    <div className="admin-products-main-container">
      <Loader loading={isLoading} />
      <div className="configure-server-roles-main-heading-container">
        <h1 style={{ marginInline: "24px", marginBlock: "12px" }}>
          Privacy Policies
        </h1>
        <div
          style={{
            display: "flex",
            gap: "1rem",
            alignItems: "center",
            marginInline: "24px",
            marginBlock: "12px",
          }}
        >
          <Input
            placeholder="Search by content"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: "25rem" }}
          />
          <Button type="primary" onClick={handleCreate}>
            + Add Policy
          </Button>
        </div>
      </div>

      <div
        style={{ marginInline: "24px", marginBlock: "12px" }}
        className="server-roles-tb-main-container table-container"
      >
        {error && (
          <div
            style={{ color: "red", textAlign: "center", marginBottom: "10px" }}
          >
            {error}
          </div>
        )}
        <Table
          columns={columns}
          dataSource={filteredData}
          pagination={filteredData?.length > 1}
          scroll={{ x: "max-content" }}
          onRow={(record) => ({
            onClick: () => {
              setSelectedRecord(record);
              setIsDetailModalVisible(true);
            },
            style: { cursor: "pointer" },
          })}
        />
      </div>

      <Modal
        title={isEditingMode ? "Edit Policy Content" : "Create Policy Content"}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setIsSubmitting(false);
        }}
        onOk={handleSubmit}
        okText={isEditingMode ? "Update" : "Create"}
        confirmLoading={isSubmitting}
        width={800}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Content"
            name="content"
            rules={[{ required: true, message: "Content is required" }]}
          >
            <ReactQuill
              theme="snow"
              style={{ height: "300px", marginBottom: "50px" }}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Policy Details"
        open={isDetailModalVisible}
        onCancel={() => setIsDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedRecord && (
          <div>
            <p>
              <strong>Created At:</strong> {selectedRecord?.createdAt}
            </p>
            <p>
              <strong>Content Preview:</strong>
            </p>
            <iframe
              srcDoc={selectedRecord.content}
              title="Privacy Policy Content"
              style={{
                width: "100%",
                height: "50vh",
                border: "1px solid #e8e8e8",
                borderRadius: "8px",
              }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PrivacyPolicy;
